import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '@etherisc/ui-kit';
import { BarChart3, Download, RefreshCw, TrendingUp } from 'lucide-react';
import { FinancialAnalyticsDashboard } from '../../components/insurance/FinancialAnalyticsDashboard';
import { 
  useClientReceivablesAging,
  useMultiClientBalanceSummary 
} from '../../hooks/insurance/useInsuranceReporting';
import { 
  useReconciliationReport,
  useCommissionSummary 
} from '../../hooks/insurance/useInsuranceBatch';
import { exportFinancialAnalytics } from '../../utils/exportUtils';

type DashboardTab = 'overview' | 'trends' | 'alerts';

export const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [selectedPeriod] = useState<string>('');
  const [asOfDate] = useState<Date>(new Date());

  const { data: agingData, refetch: refetchAging } = useClientReceivablesAging(asOfDate);
  const { data: balanceData, refetch: refetchBalance } = useMultiClientBalanceSummary({ periodId: selectedPeriod });
  const { data: reconciliationData, refetch: refetchReconciliation } = useReconciliationReport({ periodId: selectedPeriod });
  const { data: commissionData, refetch: refetchCommission } = useCommissionSummary({});

  const handleRefreshAll = () => {
    refetchAging();
    refetchBalance();
    refetchReconciliation();
    refetchCommission();
  };

  const handleExportAll = () => {
      exportFinancialAnalytics(
        agingData || [],
        balanceData || [],
        commissionData || null,
        reconciliationData || null,
        asOfDate
      );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground">
            Comprehensive financial analytics and performance insights
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportAll}
            disabled={!agingData && !balanceData && !commissionData}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Analytics
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DashboardTab)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Financial Overview
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Performance Trends
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Alerts & Actions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <FinancialAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="trends" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <TrendingUp className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">Performance Trends Coming Soon</h3>
                <p>
                  Historical trend analysis, growth metrics, and performance comparisons 
                  will be available in the next release.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Alerts & Recommended Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Dynamic alerts based on data */}
                {agingData && agingData.filter(item => item.agingBucket !== 'current').length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-5 w-5 text-yellow-600" />
                      <h4 className="font-medium text-yellow-800">Overdue Receivables Alert</h4>
                    </div>
                    <p className="text-sm text-yellow-700 mb-3">
                      {agingData.filter(item => item.agingBucket !== 'current').length} clients have overdue premiums.
                    </p>
                    <Button size="sm" variant="outline">
                      Review Overdue Accounts
                    </Button>
                  </div>
                )}

                {reconciliationData?.accounts && reconciliationData.accounts.filter(acc => parseFloat(acc.variance) !== 0).length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-5 w-5 text-red-600" />
                      <h4 className="font-medium text-red-800">Reconciliation Variances</h4>
                    </div>
                    <p className="text-sm text-red-700 mb-3">
                      {reconciliationData?.accounts?.filter(acc => parseFloat(acc.variance) !== 0).length} accounts have reconciliation variances.
                    </p>
                    <Button size="sm" variant="outline">
                      Investigate Variances
                    </Button>
                  </div>
                )}

                {commissionData?.summary && parseFloat(commissionData.summary.totalOutstanding) > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium text-blue-800">Commission Payments Due</h4>
                    </div>
                    <p className="text-sm text-blue-700 mb-3">
                      {commissionData.summary.totalOutstanding} in commission payments are outstanding.
                    </p>
                    <Button size="sm" variant="outline">
                      Process Commission Payments
                    </Button>
                  </div>
                )}

                {(!agingData || agingData.filter(item => item.agingBucket !== 'current').length === 0) &&
                 (!reconciliationData?.accounts || reconciliationData.accounts.filter(acc => parseFloat(acc.variance) !== 0).length === 0) &&
                 (!commissionData?.summary || parseFloat(commissionData.summary.totalOutstanding) === 0) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-5 w-5 text-green-600" />
                      <h4 className="font-medium text-green-800">All Systems Healthy</h4>
                    </div>
                    <p className="text-sm text-green-700">
                      No immediate actions required. All accounts are current and reconciled.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
